export type LegalBlock = string | { list: Array<string> }

export type LegalSection = {
    id: string
    title: string
    body: Array<LegalBlock>
}

export const LEGAL_UPDATED = '2026-10-26'

export const privacySections: Array<LegalSection> = [
    {
        id: 'about-us',
        title: 'Who looks after your data',
        body: [
            'Updraft Studio (“Updraft”, “we”, “us”) is a social media studio at Unit 7, Shoreditch Studios, London. We decide how the personal information described here is used, which makes us responsible for it.',
            'Questions are welcome. Email hello@updraft.studio and a human on the team will answer, usually within a working day.'
        ]
    },
    {
        id: 'what-we-keep',
        title: 'What we keep',
        body: [
            'We keep as little as we can while still being useful:',
            {
                list: [
                    'What you type into our forecast and call booking forms: your name, email, brand or website, the platforms and budget you pick, your preferred call time and your message.',
                    'Your email address, if you sign up to the Tuesday Signal.',
                    'Counts of how people use this website, such as which pages are visited. These are anonymous, use no cookies and cannot be traced back to you.',
                    'Anything you share with us during a project, such as brand files, logins you choose to give us and feedback on our work.'
                ]
            },
            'We never ask for sensitive details such as health information, and we would rather you did not send them.'
        ]
    },
    {
        id: 'why',
        title: 'Why we use it',
        body: [
            'We use your information to answer your message, prepare your free forecast, arrange calls, deliver the work you hire us for and send the newsletter you asked for.',
            'Our legal reasons are: taking steps at your request before a contract, carrying out a contract with you, your consent for the newsletter, and our legitimate interest in running and improving a small studio.'
        ]
    },
    {
        id: 'emails',
        title: 'Emails we send',
        body: [
            'When you contact us, a copy of your message is emailed to our team so we can reply. Some emails we send include open and click tracking, which tells us roughly whether a message was opened and which links were clicked. We use this only to understand whether our emails are useful.',
            'Every newsletter has an unsubscribe link at the bottom. One click and you are off the list, no questions asked.'
        ]
    },
    {
        id: 'who-else',
        title: 'Who else sees it',
        body: [
            'We do not sell or rent your information. Ever. We share it only with services that help us run the studio, and only as much as they need:',
            {
                list: [
                    'Our website, database and file storage provider, which hosts this site and stores form entries.',
                    'Our email provider, which delivers messages between us.',
                    'Platforms we manage on your behalf during a project, such as Instagram, TikTok or YouTube, under their own terms.'
                ]
            },
            'If the law requires us to share information with an authority, we will do so and only to the extent required.'
        ]
    },
    {
        id: 'how-long',
        title: 'How long we keep it',
        body: [
            'Form messages that do not turn into a project are deleted after 18 months. Client records are kept for six years after a project ends, because accounting rules say so. Newsletter addresses stay until you unsubscribe.'
        ]
    },
    {
        id: 'your-rights',
        title: 'Your rights',
        body: [
            'You can ask us to show you what we hold about you, correct it, delete it, limit how we use it or send you a copy in a portable format. You can withdraw your newsletter consent at any time.',
            'Email hello@updraft.studio to use any of these rights. If you are unhappy with our answer, you can complain to the Information Commissioner’s Office at ico.org.uk.'
        ]
    },
    {
        id: 'safety',
        title: 'Keeping it safe',
        body: [
            'Information is stored with reputable providers, sent over encrypted connections and only reachable by the people on our team who need it. No system is perfect, but we treat your details like we would want ours treated.'
        ]
    },
    {
        id: 'changes',
        title: 'If this policy changes',
        body: [
            'When we update this page, the date at the top changes. If the change is significant, we will tell newsletter subscribers in the next issue. Any questions, write to hello@updraft.studio or Updraft Studio, Unit 7, Shoreditch Studios, London.'
        ]
    }
]

export const termsSections: Array<LegalSection> = [
    {
        id: 'the-basics',
        title: 'The basics',
        body: [
            'These terms cover your use of the Updraft website and the services we provide to clients. By using the site or hiring us, you agree to them. If something here is unclear, ask us before you sign anything. We would much rather explain.'
        ]
    },
    {
        id: 'forecasts',
        title: 'Free forecasts and calls',
        body: [
            'A free forecast is a short written read of your category with a few ideas. It is ours to write and yours to keep, with no obligation to hire us. Free calls last about 20 minutes and are for advice, not for selling you a plan you do not need.'
        ]
    },
    {
        id: 'working-together',
        title: 'Working together',
        body: [
            'Each project has a written proposal setting out the plan, the deliverables, the price and the timeline. If the proposal and these terms ever disagree, the proposal wins.',
            'To do great work we need a few things from you:',
            {
                list: [
                    'Timely feedback, ideally within three working days.',
                    'Accurate information about your products, offers and claims.',
                    'Access to the accounts and files we agree we need.',
                    'One named person who can approve work on your behalf.'
                ]
            }
        ]
    },
    {
        id: 'plans-and-payment',
        title: 'Plans and payment',
        body: [
            'Plans are billed monthly or yearly in advance, as shown on our pricing page. The first quarter is a minimum commitment. After that, plans continue month to month and can be changed or paused with 30 days notice.',
            'Invoices are due within 14 days. Ad spend is never included in our fees and is paid directly to each platform.'
        ]
    },
    {
        id: 'ownership',
        title: 'Who owns what',
        body: [
            'Once an invoice is paid, the content we make for you is yours to use however you like. We keep the right to show finished, published work in our portfolio and case notes, unless you ask us not to in writing.',
            'Templates, methods and tools we developed before working with you stay ours, but you get a licence to keep using anything we built into your account.'
        ]
    },
    {
        id: 'results',
        title: 'About results',
        body: [
            'We work hard to grow your audience and your sales, and we share honest numbers every step of the way. But social platforms change their rules, audiences change their minds and the weather changes without asking. We cannot promise specific results, and nothing on this site is a guarantee.'
        ]
    },
    {
        id: 'platform-rules',
        title: 'Platform rules',
        body: [
            'Everything we post follows the rules of the platform it is posted on. If a platform suspends or limits an account for reasons outside our control, we will help you sort it out, but we are not responsible for the platform’s decisions.'
        ]
    },
    {
        id: 'liability',
        title: 'Limits of liability',
        body: [
            'Our total liability for any claim connected with our services is limited to the fees you paid us in the three months before the claim. We are not liable for indirect losses such as lost profits. Nothing in these terms limits liability that the law does not allow us to limit.'
        ]
    },
    {
        id: 'the-website',
        title: 'Using this website',
        body: [
            'You are welcome to read, share and link to anything here. Please do not copy our case notes, guides or images wholesale and pass them off as your own. The Playbook cards are free to download and use for your own brand, just not to resell.'
        ]
    },
    {
        id: 'law-and-changes',
        title: 'Law and changes',
        body: [
            'These terms are governed by the law of England and Wales. We may update them from time to time and the date at the top will change when we do. Questions about these terms? Email hello@updraft.studio and you will hear back within one working day.'
        ]
    }
]
